import { Button, Dialog, Loading } from '@/components';
import { States } from '@/helpers/common/states';
import { GatewayPriority } from '@/model/GatewayPriority';
import PaymentGateway from '@/model/PaymentGateway';
import React, { Fragment } from 'react';
import { ActionProps } from '@/components/Dialog';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { GatewayPrioritySequence } from '@/model/GatewayPrioritySequence';
import { Container } from 'reactstrap';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { CustomTable } from '@/components/Table';
import PaymentType from '@/model/PaymentType';
import { ReactComponent as Pen } from '@/assets/images/svg/pen.svg';
import { ReactComponent as Trash } from '@/assets/images/svg/lixeira.svg';
import { ShouldShowModal } from '../../types';
import { RegisterContent } from '../../components/RegisterContent';

interface StateProps {
  state: States;
  title: string | React.ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  paymentGateways: PaymentGateway[];
  formData: FormData;
  formErrors: FormErrors;
  sequences: GatewayPrioritySequence[];
  gateways: GatewayPriority[];
}

interface DispatchProps {
  onCancel: () => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
    id,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    id?: string;
  }) => void;

  onSave: () => void;
  onChangeFormInput: OnChangeFormInput;
  onChangeGateway: (index: number, paymentGatewayId: string) => void;
  onChangeSequence: (index: number, sequence: number) => void;
  onAddSequence: () => void;
  onRemoveSequence: (index: number) => void;
  onRemove: (id: string) => void;
}

type Props = StateProps & DispatchProps;

export const GatewayPriorityContainer: React.FC<Props> = ({
  state,
  title,
  visible,
  onCancel,
  onShouldShowModal,
  shouldShowModal,
  onSave,
  paymentGateways,
  formData,
  formErrors,
  onChangeFormInput,
  sequences,
  onChangeGateway,
  onChangeSequence,
  onAddSequence,
  onRemoveSequence,
  gateways,
  onRemove,
}): JSX.Element => {
  const renderActionDialogToCancel: ActionProps = {
    title: 'Cancelar',
    onClick: (): void => onCancel(),
    theme: 'noneBorder',
  };
  const toPaymentTypeString = (paymentType: PaymentType): string => {
    let value = '';
    switch (paymentType) {
      case PaymentType.CREDIT_CARD:
        value = 'Cartão de Crédito';
        break;
      case PaymentType.DEBIT_CARD:
        value = 'Cartão de Débito';
        break;
      case PaymentType.PIX:
        value = 'PIX';
        break;
      case PaymentType.BANK_SLIP:
        value = 'Boleto';
        break;
      default:
        break;
    }
    return value;
  };
  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onCancel}
        position={'center'}
        isContentWithCard={true}
        actions={[
          {
            [ShouldShowModal.register]: renderActionDialogToCancel,
          }[shouldShowModal],
          {
            [ShouldShowModal.register]: {
              title: 'Salvar',
              onClick: (): void => onSave(),
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.register]: (
              <RegisterContent
                paymentGateways={paymentGateways}
                formData={formData}
                formErrors={formErrors}
                onChangeFormInput={onChangeFormInput}
                sequences={sequences}
                onChangeGateway={onChangeGateway}
                onChangeSequence={onChangeSequence}
                onAddSequence={onAddSequence}
                onRemoveSequence={onRemoveSequence}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between" style={{ paddingBottom: '30px' }}>
          <h5 className="pageTitle">Configurações</h5>
          <div className="button-filter-container">
            <Button
              size="md"
              title="+ Cadastrar"
              onClick={(): void => {
                onShouldShowModal({
                  value: ShouldShowModal.register,
                  newTitleModal: 'Cadastrar',
                });
              }}
            />
          </div>
        </div>
        {gateways?.map((data, index: number) => (
          <SuperCollapse
            key={`gateway-table-${index}-${data.id}`}
            width={'100%'}
            // title={toPaymentTypeString(data.paymentType)}
            title={
              <CustomTable
                numberRowsPerPage={0}
                progressPending={false}
                theme="secondaryWithoutBorder"
                columns={[
                  {
                    name: 'Gateway',
                    selector: row => row.name,
                    width: '90%',
                  },
                  {
                    name: 'Ações',
                    selector: row => row.action,
                    width: '10%',
                  },
                ]}
                data={[
                  {
                    name: toPaymentTypeString(data.paymentType),
                    action: (
                      <div className="d-flex">
                        <Pen
                          onClick={(): void => {
                            onShouldShowModal({
                              value: ShouldShowModal.register,
                              newTitleModal: 'Cadastrar',
                              id: data.id,
                            });
                          }}
                          className="mr-4 svg-icon action-icon"
                        />
                        <Trash
                          onClick={(): void => onRemove(data.id)}
                          className="mr-2 svg-icon action-icon svg-icon-trash"
                        />
                      </div>
                    ),
                  },
                ]}
              />
            }
            content={
              <CustomTable
                numberRowsPerPage={0}
                progressPending={false}
                theme="secondaryWithoutBorder"
                columns={[
                  {
                    name: 'Gateway',
                    selector: row => row.name,
                    width: '90%',
                  },
                  {
                    name: 'Prioridade',
                    selector: row => row.sequence,
                    width: '10%',
                  },
                ]}
                data={
                  data.gatewayPrioritySequences && data.gatewayPrioritySequences.length > 0
                    ? data.gatewayPrioritySequences.map(gatewayPrioritySequence => ({
                        name: <strong>{gatewayPrioritySequence.paymentGateway.name}</strong>,
                        sequence: gatewayPrioritySequence.sequence || '---',
                      }))
                    : []
                }
              />
            }
          />
        ))}
      </Container>
    </Fragment>
  );
};
